import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLang } from "../../contexts/LanguageContext";
import './Transcription.css';

const convertirAEnlacesJSX = (lemmatizationArray, phonologizationArray, handleWordClick, currentWordId, linkIdCounter, bookHash) => {
  let elements = [];
  let currentParagraph = [];
  let currentTag = 'paragraph';

  const closeParagraph = (tag, key) => {
    if (currentParagraph.length === 0) return;

    const tagMap = {
        't': 'title',
        's': 'subtitle',
        'r': 'resume',
        'v': 'stanza'
    };
    const className = tagMap[tag] || 'paragraph';

    elements.push(
      <p className={className} key={key}>
        {currentParagraph}
      </p>
    );
    currentParagraph = [];
  };

  lemmatizationArray.forEach((item, i) => {
    if (Array.isArray(item)) {
      const [word, lemma, pos, meta] = item;
      const phonology = phonologizationArray[i] && phonologizationArray[i][0] ? phonologizationArray[i][0] : '';
      
      // --- SOPORTE PARA IMÁGENES RSM2 ---
      if (meta?.tag === 'img' || pos === 'img') {
        if (currentParagraph.length > 0) {
          closeParagraph(currentTag, i);
        }

        let imageSrc = meta?.src || '';
        if (imageSrc && !imageSrc.startsWith('http://') && !imageSrc.startsWith('https://')) {
          const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.readerum.com';
          
          let finalPath = imageSrc;
          if (!imageSrc.includes('storage/book_files/')) {
            // Si es un nombre de archivo simple, lo enrutamos mediante el proxy seguro
            finalPath = `storage/book_files/${bookHash}/${imageSrc}`;
          }

          const separator = finalPath.startsWith('/') ? '' : '/';
          imageSrc = `${baseUrl}${separator}${finalPath}`;
        }

        elements.push(
          <div className="rsm-image-container" key={`img-${i}`}>
            <img 
              src={imageSrc} 
              alt={meta?.alt || 'Readerum Illustration'} 
              className="rsm-image img-fluid"
            />
            {meta?.alt && (
              <p className="rsm-image-caption">
                {meta.alt}
              </p>
            )}
          </div>
        );
        return;
      }
      // ----------------------------------

      // Lógica de detección de etiqueta: usa la actual, o intenta heredar de vecinos si es espacio/punct
      let tag = meta?.tag;
      if (!tag) {
          const prev = lemmatizationArray[i-1];
          const next = lemmatizationArray[i+1];
          
          let prevTag = (prev && Array.isArray(prev)) ? prev[3]?.tag : null;
          let nextTag = (next && Array.isArray(next)) ? next[3]?.tag : null;
          
          // No heredar la etiqueta 'img' ya que es un bloque multimedia independiente
          if (prevTag === 'img') prevTag = null;
          if (nextTag === 'img') nextTag = null;
          
          if (prevTag) tag = prevTag;
          else if (nextTag) tag = nextTag;
          else tag = 'paragraph';
      }

      // Si la etiqueta cambia (o iniciamos), cerramos el párrafo anterior
      if (tag !== currentTag && currentParagraph.length > 0) {
          closeParagraph(currentTag, i);
          currentTag = tag;
      }

      if (pos === 'space') {
        if (word === '\n' || word === '\r\n' || word === ' \n') {
          if (currentTag === 'v') {
            currentParagraph.push(<br key={i} />);
          } else {
            currentParagraph.push(' ');
          }
        } else if (word === '\n\n' || word === '\r\n\r\n') {
           closeParagraph(currentTag, i);
        } else {
          currentParagraph.push(word);
        }
      } else if (pos === 'punct' || pos === 'sym' || ['—', '-', '–', '_'].includes(word) || word === '') {
        currentParagraph.push(word);
      } else {
        const anchor = (
          <a
            className={`mostrarModal ${linkIdCounter === currentWordId ? 'current-word' : ''}`}
            href="#"
            key={linkIdCounter}
            id={linkIdCounter}
            onClick={(e) => {
              e.preventDefault();
              handleWordClick(lemma, pos, linkIdCounter);
            }}
          >
            {word}
            <span className="p">{pos}</span>
            <span className="fon" style={{ display: 'none' }}>{phonology}</span>
          </a>
        );

        currentParagraph.push(anchor);
        linkIdCounter++;
      }
      
      currentTag = tag;
    } else {
      currentParagraph.push(item);
    }
  });

  closeParagraph(currentTag, 'final');
  return elements;
};

// Transcription component
const Transcription = ({ lemmatizationArray, phonologizationArray, handleWordClick, currentWordId, fontSize, showLemma, bookHash }) => {
  const { t } = useLang();
  const containerRef = useRef(null);
  const [linkIdCounter, setLinkIdCounter] = useState(1);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const autoScrollRef = useRef(true);
  const suppressNextScrollRef = useRef(false);
  const suppressTimeoutRef = useRef(null);
  const lastLineTopRef = useRef(null);

  useEffect(() => {
    autoScrollRef.current = autoScrollEnabled;
    if (!autoScrollEnabled) {
      lastLineTopRef.current = null;
    }
  }, [autoScrollEnabled]);

  useEffect(() => {
    // Reset linkIdCounter when lemmatizationArray changes
    setLinkIdCounter(1);
  }, [lemmatizationArray]);

  useEffect(() => {
    const transcription = document.getElementById("transcription");
    const sizeClasses = ["uTextA", "uTextB", "uTextC", "uTextD", "uTextE"];
    transcription.classList.remove(...sizeClasses);
    transcription.classList.add(sizeClasses[fontSize]);
  }, [fontSize]);

  useEffect(() => {
    const phonologySpans = document.querySelectorAll(".fon");
    phonologySpans.forEach(span => {
      span.style.display = showLemma ? 'inline' : 'none';
    });
  }, [showLemma]);

  useEffect(() => {
    if (!autoScrollRef.current) return;
    if (!currentWordId || currentWordId === -1) return;
    const currentAnchor = document.getElementById(String(currentWordId));
    if (!currentAnchor) return;

    const anchorRect = currentAnchor.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const computedStyles = window.getComputedStyle(currentAnchor);
    const parsedLineHeight = parseFloat(computedStyles.lineHeight);
    const lineHeight = Number.isFinite(parsedLineHeight) && parsedLineHeight > 0
      ? parsedLineHeight
      : Math.max(anchorRect.height || 0, 20);
    const lastLineTop = lastLineTopRef.current;

    const lineThreshold = lineHeight * 0.75;
    const lineChanged = lastLineTop !== null && Math.abs(anchorRect.top - lastLineTop) >= lineThreshold;

    const ensureVisible = () => {
      const topThreshold = 80;
      const bottomThreshold = 80;
      const needsScroll =
        anchorRect.top < topThreshold || anchorRect.bottom > viewportHeight - bottomThreshold;

      if (needsScroll) {
        suppressNextScrollRef.current = true;
        if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
        const target = window.scrollY + anchorRect.top - (viewportHeight / 2);
        window.scrollTo({ top: Math.max(target, 0), behavior: 'smooth' });
        suppressTimeoutRef.current = setTimeout(() => {
          suppressNextScrollRef.current = false;
        }, 350);
        return viewportHeight / 2;
      }
      return anchorRect.top;
    };

    if (lineChanged) {
      const direction = anchorRect.top > lastLineTop ? 1 : -1;
      suppressNextScrollRef.current = true;
      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
      window.scrollBy({ top: direction * lineHeight, behavior: 'smooth' });
      suppressTimeoutRef.current = setTimeout(() => {
        suppressNextScrollRef.current = false;
      }, 250);
      lastLineTopRef.current = Math.round(anchorRect.top - direction * lineHeight);
      return;
    }

    const updatedTop = ensureVisible();

    lastLineTopRef.current = Math.round(updatedTop);
  }, [currentWordId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleUserScroll = (event) => {
      if (!event) return;
      if (!event.isTrusted) return; // ignora scroll programático
      if (suppressNextScrollRef.current) return;
      if (!autoScrollRef.current) return;
      setAutoScrollEnabled(false);
      lastLineTopRef.current = null;
    };

    const handleWheel = () => {
      if (suppressNextScrollRef.current) return;
      if (!autoScrollRef.current) return;
      setAutoScrollEnabled(false);
      lastLineTopRef.current = null;
    };

    const handleTouchMove = () => {
      if (suppressNextScrollRef.current) return;
      if (!autoScrollRef.current) return;
      setAutoScrollEnabled(false);
      lastLineTopRef.current = null;
    };

    container.addEventListener('scroll', handleUserScroll, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('scroll', handleUserScroll, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleUserScroll);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('scroll', handleUserScroll);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (suppressTimeoutRef.current) {
        clearTimeout(suppressTimeoutRef.current);
      }
    };
  }, []);

  const handleEnableAutoScroll = useCallback(() => {
    lastLineTopRef.current = null;
    setAutoScrollEnabled(true);
    const currentAnchor = currentWordId ? document.getElementById(String(currentWordId)) : null;
    if (currentAnchor) {
      suppressNextScrollRef.current = true;
      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
      currentAnchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Aumentamos el tiempo de supresión a 1000ms para permitir que finalice la animación 
      // de desplazamiento suave incluso cuando la palabra activa está muy lejos.
      suppressTimeoutRef.current = setTimeout(() => {
        suppressNextScrollRef.current = false;
      }, 1000);
      
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      lastLineTopRef.current = Math.round(viewportHeight / 2);
    }
  }, [currentWordId]);

  const transcriptionContent = convertirAEnlacesJSX(
    lemmatizationArray,
    phonologizationArray,
    handleWordClick,
    currentWordId,
    linkIdCounter,
    bookHash
  );

  return (
    <div id="transcription" ref={containerRef}>
      {transcriptionContent}
      {!autoScrollEnabled && (
        <div className="autoscroll-toggle">
          <button type="button" className="autoscroll-button" onClick={handleEnableAutoScroll}>
            {t('CHAPTER_AUTOSCROLL_RESUME')}
          </button>
        </div>
      )}
    </div>
  );
};

export default Transcription;
